import { ThreeHeadedDragonCardPosition, ThreeHeadedDragonOutcome, ThreeHeadedDragonSidePosition } from './three-headed-dragon.model';
import { GameType } from '../../../models/communication/app-enums.model';
import { CardPosition, HandValueKey, Outcome } from '../../../models/communication/games.model';
import { GameBase } from '../../game-base';
import { ThreeHeadedDragonFlow } from '../../game-flows/three-headed-dragon.game.flow';
import { GameConfig } from '../../models/game-config.model';
import { SideState } from '../../models/game-view.model';

export const threeHeadedDragonConfig: GameConfig = {
    sides: [
        {
            position: ThreeHeadedDragonSidePosition.Dragon,
            hands: [
                {
                    label: $localize`RED`,
                    handValueKey: 'redDragonHandValue' as HandValueKey,

                    cards: [
                        {
                            position: ThreeHeadedDragonCardPosition.RedDragonCard,
                            isCompact: true
                        }
                    ]
                },
                {
                    label: $localize`BLACK`,
                    handValueKey: 'blackDragonHandValue' as HandValueKey,

                    cards: [
                        {
                            position: ThreeHeadedDragonCardPosition.BlackDragonCard,
                            isCompact: true
                        }
                    ]
                },
                {
                    label: $localize`GOLDEN`,
                    handValueKey: 'goldenDragonHandValue' as HandValueKey,

                    cards: [
                        {
                            position: ThreeHeadedDragonCardPosition.GoldenDragonCard,
                            isCompact: true
                        }
                    ]
                }
            ]
        },
        {
            position: ThreeHeadedDragonSidePosition.Tiger,
            hands: [
                {
                    label: $localize`TIGER`,
                    handValueKey: 'tigerHandValue' as HandValueKey,

                    cards: [
                        {
                            position: ThreeHeadedDragonCardPosition.TigerCard,
                            isCompact: true
                        }
                    ]
                }
            ]
        }
    ]
};

export class ThreeHeadedDragonGame extends GameBase {
    static override config = threeHeadedDragonConfig;
    static override gameType = GameType.ThreeHeadedDragon;
    override flow: ThreeHeadedDragonFlow = new ThreeHeadedDragonFlow(this);

    override getFieldLabel(cardPosition: CardPosition): string {
        switch (cardPosition as ThreeHeadedDragonCardPosition) {
            case ThreeHeadedDragonCardPosition.TigerCard: {
                return $localize`TIGER FIELD`;
            }
            case ThreeHeadedDragonCardPosition.GoldenDragonCard: {
                return $localize`GOLDEN DRAGON FIELD`;
            }
            case ThreeHeadedDragonCardPosition.BlackDragonCard: {
                return $localize`BLACK DRAGON FIELD`;
            }
            case ThreeHeadedDragonCardPosition.RedDragonCard: {
                return $localize`RED DRAGON FIELD`;
            }
            default:
                // eslint-disable-next-line no-console
                console.warn(`Unknown card position: ${String(cardPosition)}`);
                return '';
        }
    }

    override getOutcomeLabel(outcome: Outcome): string {
        switch (outcome as ThreeHeadedDragonOutcome) {
            case ThreeHeadedDragonOutcome.DragonWin: {
                return $localize`DRAGON WIN`;
            }
            case ThreeHeadedDragonOutcome.TigerWin: {
                return $localize`TIGER WIN`;
            }
            case ThreeHeadedDragonOutcome.Tie: {
                return $localize`TIE`;
            }
            default:
                // eslint-disable-next-line no-console
                console.warn(`Unknown outcome: ${String(outcome)}`);
                return '';
        }
    }

    override setOutcomeStates(outcome: Outcome): void {
        switch (outcome as ThreeHeadedDragonOutcome) {
            case ThreeHeadedDragonOutcome.Tie:
                this.updateSides(SideState.Tie);
                break;
            case ThreeHeadedDragonOutcome.TigerWin:
                this.updateSide(ThreeHeadedDragonSidePosition.Tiger, SideState.Win);
                break;
            case ThreeHeadedDragonOutcome.DragonWin:
                this.updateSide(ThreeHeadedDragonSidePosition.Dragon, SideState.Win);
        }
    }

    override onEnterShrinkMode(): void {
        super.onEnterShrinkMode();

        this.getSide(ThreeHeadedDragonSidePosition.Dragon).label = $localize`3 HEADED DRAGON`;
        this.getSide(ThreeHeadedDragonSidePosition.Tiger).label = $localize`TIGER`;

        for (const card of this.view.cards) {
            card.isCompact = false;
        }
    }

    override onExitShrinkMode(): void {
        super.onExitShrinkMode();

        this.getSide(ThreeHeadedDragonSidePosition.Dragon).label = '';
        this.getSide(ThreeHeadedDragonSidePosition.Tiger).label = '';

        for (const card of this.view.cards) {
            card.isCompact = true;
        }
    }
}
